import { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import Navbar from "../components/Navbar";
import OpportunityCard from "../components/OpportunityCard";
import LoadingSpinner from "../components/LoadingSpinner";
import EmptyState from "../components/EmptyState";

const Hackathons = () => {
  const [hackathons, setHackathons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({ mode: "all", prize: "all" });
  const [sortOption, setSortOption] = useState("none");
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 12;

  useEffect(() => {
    const fetchHackathons = async () => {
      try {
        const snap = await getDocs(collection(db, "hackathons"));
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setHackathons(data);
      } catch (error) {
        console.error("Error fetching hackathons:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchHackathons();
  }, []);

  let filtered = hackathons.filter((h) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      h.title?.toLowerCase().includes(q) || h.organizer?.toLowerCase().includes(q);

    const matchesMode =
      filters.mode === "all" ||
      (filters.mode === "online" && h.mode === "Online") ||
      (filters.mode === "offline" && h.mode === "Offline");

    const matchesPrize =
      filters.prize === "all" ||
      (filters.prize === ">50000" && h.prize && h.prize > 50000) ||
      (filters.prize === "<50000" && h.prize && h.prize <= 50000);

    return matchesSearch && matchesMode && matchesPrize;
  });

  let sorted = [...filtered];
  if (sortOption === "prize") sorted.sort((a, b) => (b.prize || 0) - (a.prize || 0));
  if (sortOption === "deadline") sorted.sort((a, b) => new Date(a.deadline) - new Date(b.deadline));

  const totalPages = Math.ceil(sorted.length / perPage);
  const paginated = sorted.slice((currentPage - 1) * perPage, currentPage * perPage);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <LoadingSpinner text="Loading hackathons..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="page-container">
        <div className="mb-8">
          <h1 className="section-title">Hackathons</h1>
          <p className="text-gray-500 mt-1">{sorted.length} hackathons available</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <div className="relative sm:col-span-2 lg:col-span-4">
            <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by title or organizer..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              className="input-field pl-11"
            />
          </div>

          <select
            value={filters.mode}
            onChange={(e) => { setFilters({ ...filters, mode: e.target.value }); setCurrentPage(1); }}
            className="filter-select"
          >
            <option value="all">Mode</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
          </select>

          <select
            value={filters.prize}
            onChange={(e) => { setFilters({ ...filters, prize: e.target.value }); setCurrentPage(1); }}
            className="filter-select"
          >
            <option value="all">Prize</option>
            <option value=">50000">Above ₹50,000</option>
            <option value="<50000">Up to ₹50,000</option>
          </select>

          <select
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
            className="filter-select"
          >
            <option value="none">Sort by</option>
            <option value="prize">Prize (High to Low)</option>
            <option value="deadline">Deadline (Soonest)</option>
          </select>
        </div>

        {paginated.length === 0 ? (
          <EmptyState
            title="No hackathons found"
            message="Try adjusting your filters or check back later for new hackathons."
          />
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 animate-fade-in">
              {paginated.map((h) => (
                <OpportunityCard
                  key={h.id}
                  opportunity={h}
                  type="hackathon"
                  showBookmark={false}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-10">
                <button
                  className="button btn-secondary text-sm"
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600 px-3">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="button btn-secondary text-sm"
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Hackathons;
